import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { formatBlockedAnswerPhone, normalizeBlockedAnswerPhone } from "../lib/blockedAnswers.mjs";

interface BlockedAnswerRow {
  id: string;
  client_id: string;
  name: string;
  phone_number: string;
}

function getWriteErrorMessage(error: { code?: string; message?: string } | null): string {
  if (error?.code === "23505") return "This phone number is already blocked.";
  return error?.message || "Unable to save this blocked number.";
}

export default function BlockedAnswers({
  clientId,
  embedded = false,
}: {
  clientId: string;
  embedded?: boolean;
}) {
  const [rows, setRows] = useState<BlockedAnswerRow[]>([]);
  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadBlockedAnswers = async () => {
    setLoading(true);
    const { data, error: loadError } = await supabase
      .from("blocked_answer_numbers")
      .select("id, client_id, name, phone_number")
      .eq("client_id", clientId)
      .order("name", { ascending: true });

    if (loadError) {
      setError(loadError.message);
    } else {
      setRows((data || []) as BlockedAnswerRow[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    void loadBlockedAnswers();
  }, [clientId]);

  const validateDraft = (): string | null => {
    if (!name.trim()) return "Name is required.";
    if (!normalizeBlockedAnswerPhone(phoneNumber)) {
      return "Enter an international phone number starting with + and its country code.";
    }
    return null;
  };

  const resetDraft = () => {
    setEditingId(null);
    setName("");
    setPhoneNumber("");
  };

  const saveBlockedAnswer = async () => {
    const validationError = validateDraft();
    if (validationError) {
      setError(validationError);
      return;
    }

    const normalizedPhone = normalizeBlockedAnswerPhone(phoneNumber)!;
    setSaving(true);
    setError(null);
    const payload = { name: name.trim(), phone_number: normalizedPhone };
    const result = editingId
      ? await supabase
          .from("blocked_answer_numbers")
          .update(payload)
          .eq("id", editingId)
          .eq("client_id", clientId)
      : await supabase
          .from("blocked_answer_numbers")
          .insert({ ...payload, client_id: clientId });

    if (result.error) {
      setError(getWriteErrorMessage(result.error));
      setSaving(false);
      return;
    }

    resetDraft();
    await loadBlockedAnswers();
    setSaving(false);
  };

  const startEditing = (row: BlockedAnswerRow) => {
    setEditingId(row.id);
    setName(row.name);
    setPhoneNumber(formatBlockedAnswerPhone(row.phone_number));
    setError(null);
  };

  const deleteBlockedAnswer = async (row: BlockedAnswerRow) => {
    if (!window.confirm("Remove this blocked number?")) return;

    setSaving(true);
    setError(null);
    const { error: deleteError } = await supabase
      .from("blocked_answer_numbers")
      .delete()
      .eq("id", row.id)
      .eq("client_id", clientId);
    if (deleteError) {
      setError(deleteError.message);
    } else {
      if (editingId === row.id) resetDraft();
      await loadBlockedAnswers();
    }
    setSaving(false);
  };

  const containerClass = embedded
    ? "space-y-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    : "space-y-5 rounded-xl bg-white p-6 shadow";

  return (
    <section className={containerClass}>
      <div>
        <h2 className="text-lg font-semibold text-slate-800">Blocked answers</h2>
        <p className="mt-1 text-sm text-slate-600">
          The bot records messages from these numbers but does not answer automatically.
        </p>
      </div>

      {error && <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      <div className="grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-[1fr_1fr_auto]">
        <label className="text-sm font-medium text-slate-700">
          Name
          <input value={name} onChange={(event) => setName(event.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2" placeholder="e.g. Front desk" />
        </label>
        <label className="text-sm font-medium text-slate-700">
          Phone number
          <input value={phoneNumber} onChange={(event) => setPhoneNumber(event.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2" placeholder="+65 9183 0250" inputMode="tel" />
        </label>
        <div className="flex items-end gap-2">
          <button type="button" onClick={() => void saveBlockedAnswer()} disabled={saving} className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
            {saving ? "Saving…" : editingId ? "Save" : "Add"}
          </button>
          {editingId && <button type="button" onClick={resetDraft} disabled={saving} className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700">Cancel</button>}
        </div>
      </div>

      {loading ? <p className="text-sm text-slate-500">Loading blocked numbers…</p> : rows.length === 0 ? <p className="rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-500">No blocked numbers yet.</p> : (
        <ul className="divide-y divide-slate-200 rounded-xl border border-slate-200">
          {rows.map((row) => <li key={row.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <div><p className="font-medium text-slate-800">{row.name}</p><p className="font-mono text-sm text-slate-500">{formatBlockedAnswerPhone(row.phone_number)}</p></div>
            <div className="flex gap-2"><button type="button" onClick={() => startEditing(row)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700">Edit</button><button type="button" onClick={() => void deleteBlockedAnswer(row)} disabled={saving} className="rounded-lg border border-red-300 px-3 py-1.5 text-sm text-red-700 disabled:opacity-50">Delete</button></div>
          </li>)}
        </ul>
      )}
    </section>
  );
}
